import { identity } from "lodash";
import { makeAutoObservable } from "mobx";
import {
  addGeneralMeetingDocuments,
  DbotEvent,
  DecisionContext,
  DbotEventDispatcher,
} from "../common/commonDomain";
import { GroupMethod } from "../../types/Transactions";

import { TMessage } from "../../i18n/tt";
import { DecisionType } from "../../common/decisions";
import { BooleanFieldModel, NumberFieldModel, RadioFieldModel } from "../../common/commonForms";

export type GroupContributionCompany = {
  name: string;
  organizationnumber: string;
  howToSettle: GroupMethod;
  value?: number;
};

export type EditCompaniesState =
  | { type: "add"; form: ContributionCompanyForm }
  | { type: "edit"; company: GroupContributionCompany; form: ContributionCompanyForm }
  | { type: "remove"; company: GroupContributionCompany };

export type GroupContributionByGeneralMeeting = {
  type: DecisionType.groupContributionByGeneralMeeting;
  resolutionBy: string;
  usePowerOfAttorney?: boolean;
  groupContributionCompanies: GroupContributionCompany[];
};

function withoutExistingCompanies<
  E extends { organizationnumber: string },
  S extends { organizationnumber: string },
>(existing: E[], suggestions: S[]): S[] {
  return suggestions.filter(
    (s) => !existing.some((e) => e.organizationnumber === s.organizationnumber),
  );
}

export class GroupContributionByGeneralMeetingForm {
  readonly type = DecisionType.groupContributionByGeneralMeeting;
  dispatch: DbotEventDispatcher;
  context: DecisionContext;
  usePowerOfAttorney: BooleanFieldModel;
  companies: GroupContributionCompany[];
  editCompaniesState?: EditCompaniesState;
  validationError?: TMessage;

  constructor(
    dispatch: DbotEventDispatcher,
    context: DecisionContext,
    data?: GroupContributionByGeneralMeeting,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.dispatch = dispatch;
    this.context = context;
    this.usePowerOfAttorney = new BooleanFieldModel({
      value: data?.usePowerOfAttorney,
      required: true,
    });
    this.companies = data?.groupContributionCompanies ?? [];
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid() {
    return this.usePowerOfAttorney.isValid && !this.validationError;
  }

  validate() {
    this.validationError = undefined;
    this.usePowerOfAttorney.validate();
    if (this.companies.length === 0) {
      this.validationError = { key: "decisions.dividend.groupContribution.add-company.missing" };
    }
    return this.isValid;
  }

  setUsePowerOfAttorney(value: boolean) {
    this.usePowerOfAttorney.setValue(value);
    if (!value) {
      this.dispatch(addGeneralMeetingDocuments());
    }
  }

  openAddCompanyDialog() {
    this.editCompaniesState = {
      type: "add",
      form: new ContributionCompanyForm({
        search: (input) => {
          if (input) {
            this.dispatch({ type: "dbot/search-companies", input, source: this });
          }
        },
      }),
    };
  }

  openEditCompanyDialog(company: GroupContributionCompany) {
    this.editCompaniesState = {
      type: "edit",
      company,
      form: new ContributionCompanyForm({ data: company }),
    };
  }

  openConfirmRemoveDialog(company: GroupContributionCompany) {
    this.editCompaniesState = {
      type: "remove",
      company,
    };
  }

  submitEditCompaniesState() {
    switch (this.editCompaniesState?.type) {
      case "add":
        if (this.editCompaniesState.form.validate()) {
          this.companies.push(this.editCompaniesState.form.toJSON());
          this.clearEditCompaniesState();
        }
        break;
      case "edit":
        if (this.editCompaniesState.form.validate()) {
          this.companies[this.companies.indexOf(this.editCompaniesState.company)] =
            this.editCompaniesState.form.toJSON();
          this.clearEditCompaniesState();
        }
        break;
      case "remove":
        this.companies.splice(this.companies.indexOf(this.editCompaniesState.company), 1);
        this.clearEditCompaniesState();
        break;
    }
  }

  clearEditCompaniesState() {
    this.editCompaniesState = undefined;
  }

  handleEvent(event: DbotEvent) {
    if (
      event.type === "dbot/search-companies-result" &&
      event.source === this &&
      this.editCompaniesState?.type === "add"
    ) {
      this.editCompaniesState.form.suggestedCompanies = withoutExistingCompanies(
        this.companies,
        event.result,
      );
    }
  }
}

export class ContributionCompanyForm {
  searchInput: string;
  howToSettle: RadioFieldModel<GroupMethod>;
  value: NumberFieldModel;
  suggestedCompanies: { name: string; organizationnumber: string }[] = [];
  selectedCompany?: { name: string; organizationnumber: string };
  search?: (input?: string) => void;
  companyError?: TMessage;

  constructor({
    data,
    search,
  }: {
    data?: GroupContributionCompany;
    search?: (input?: string) => void;
  }) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.search = search;
    this.selectedCompany = data
      ? { name: data.name, organizationnumber: data.organizationnumber }
      : undefined;
    this.searchInput = this.selectedCompany?.name ?? "";
    this.howToSettle = new RadioFieldModel({
      value: data?.howToSettle ?? GroupMethod.ESTABLISH_CLAIM,
      options: Object.values(GroupMethod),
      idFn: identity,
      labelFn: (v) => ({ key: `decisions.dividend.groupContribution.howToSettle.${v}` }),
    });
    this.value = new NumberFieldModel({
      required: true,
      value: data?.value,
      isPositiveOrZero: true,
    });
  }

  get isValid(): boolean {
    return Boolean(this.selectedCompany) && this.value.isValid;
  }

  setSearchInput(input: string) {
    this.searchInput = input;
    this.search?.(input);
  }

  select(c: { name: string; organizationnumber: string }) {
    this.selectedCompany = c;
    this.searchInput = c.name;
  }

  validate(): boolean {
    this.value.validate();
    this.companyError = this.selectedCompany ? undefined : { key: "required" };
    return this.isValid;
  }

  toJSON(): GroupContributionCompany {
    return {
      name: this.selectedCompany?.name ?? "",
      organizationnumber: this.selectedCompany?.organizationnumber ?? "",
      howToSettle: this.howToSettle.value,
      value: this.value.parsedValue,
    };
  }
}
